import { doc, getDoc, setDoc, collection, query, where, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { IOrganisation, MemberRole } from '../../types/organisation';
import { sendOrganisationInvite } from '../../services/emailService';
import { v4 as uuidv4 } from 'uuid';
import { AnonymousCodeParams, AnonymousCodeResult } from './types';
import { toDate } from '../../utilities/timestampUtils';

/**
 * Verify an access code
 */
export async function verifyAccessCode(accessCode: string) {
  try {
    const accessCodesRef = collection(db, 'accessCodes');
    const q = query(
      accessCodesRef, 
      where('code', '==', accessCode),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const codeData = querySnapshot.docs[0].data();
    const now = new Date();
    const expirationDate = toDate(codeData.expiresAt);

    if (!expirationDate || now > expirationDate) {
      return null;
    }

    return {
      organisationId: codeData.organisationId,
      role: codeData.role,
    };
  } catch (error) {
    console.error('Error verifying access code:', error);
    throw error;
  }
}

/**
 * Invite a member by email
 */
export async function inviteMemberByEmail(
  orgId: string,
  email: string,
  role: MemberRole
): Promise<string> {
  try {
    // Get organisation type first
    const orgDoc = await getDoc(doc(db, 'Organisations', orgId));
    if (!orgDoc.exists()) {
      throw new Error('Organisation not found');
    }
    const orgData = orgDoc.data() as IOrganisation;

    // Generate access code
    const accessCode = uuidv4();
    
    // Create access code document
    const accessCodeRef = doc(db, 'accessCodes', accessCode);
    await setDoc(accessCodeRef, {
      code: accessCode,
      organisationId: orgId,
      role: role,
      email,
      isActive: true,
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
    });

    // Generate the invite link
    const inviteLink = `${window.location.origin}/join-org/${accessCode}`;

    // Send invitation email
    await sendOrganisationInvite(
      email,
      orgData.name,
      role,
      inviteLink
    );

    return accessCode;
  } catch (error) {
    console.error('Error inviting member:', error);
    throw error;
  }
}

/**
 * Generate an access code for an organisation
 */
export async function generateAccessCode(
  orgId: string,
  role: MemberRole
): Promise<string> {
  try {
    // Get organisation type first
    const orgDoc = await getDoc(doc(db, 'Organisations', orgId));
    if (!orgDoc.exists()) {
      throw new Error('Organisation not found');
    }
    const orgData = orgDoc.data() as IOrganisation;

    // Map role if organisation is a school
    let mappedRole = role;
    if (orgData.type === 'school') {
      mappedRole = role;
    }

    const accessCode = uuidv4().slice(0, 8);
    
    // Create access code document
    const accessCodeRef = doc(db, 'accessCodes', accessCode);
    await setDoc(accessCodeRef, {
      code: accessCode,
      organisationId: orgId,
      role: mappedRole,
      isActive: true,
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
    });

    // Return the full invite link
    return `${window.location.origin}/join-org/${accessCode}`;
  } catch (error) {
    console.error('Error generating access code:', error);
    throw error;
  }
}

/**
 * Generate an anonymous member code (temp user + access code + generatedCodes in one batch).
 */
export async function generateAnonymousMemberCode(
  params: AnonymousCodeParams
): Promise<AnonymousCodeResult> {
  try {
    const { orgId, identifier, animalEmoji, groupId, moderatorEmail } = params;

    const accessCode = uuidv4().slice(0, 8);
    const tempUserId = `temp_${accessCode}`;
    const userName = `${animalEmoji} ${identifier}`;
    const link = `${window.location.origin}/join-org/${accessCode}`;

    const userRef = doc(db, 'Users', tempUserId);
    const accessCodeRef = doc(db, 'accessCodes', accessCode);
    const generatedCodeRef = doc(db, 'Organisations', orgId, 'generatedCodes', accessCode);

    const batch = writeBatch(db);

    batch.set(userRef, {
      userKey: tempUserId,
      userProfile: {
        userName,
        bio: '',
        profileImage: '',
        restrictAutoAdd: true,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isTemporary: true,
      accessCode,
      organisationId: orgId,
      groupIds: [groupId],
      moderatorEmail,
    });

    batch.set(accessCodeRef, {
      code: accessCode,
      organisationId: orgId,
      role: 'member',
      isAnonymous: true,
      identifier: userName,
      animalEmoji,
      tempUserId,
      groupId,
      isActive: true,
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
      moderatorEmail,
    });

    batch.set(generatedCodeRef, {
      code: accessCode,
      link,
      animalEmoji,
      identifier: userName,
      tempUserId,
      groupId,
      createdAt: serverTimestamp(),
      moderatorEmail,
    });

    await batch.commit();

    return {
      code: accessCode,
      link,
      animalEmoji,
      identifier: userName,
      tempUserId,
      groupId,
      moderatorEmail,
    };
  } catch (error) {
    console.error('[OrganisationManager] Error generating anonymous student code:', error);
    throw error;
  }
}
